import {
  TableSortLabel,
  Paper,
  Grid,
  Autocomplete,
  TextField,
  Alert,
  Stack,
  IconButton,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  getSelectedTimePeriod,
  roundStringNumberToInt,
} from "../../helpers/businessleaderDashboardHelpers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import { additionalQueriestableHeadings05 } from "./AdditionalQueriesConstants";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../../features/userSlice";
import store from "../../../../../../store";
import { useSelector } from "react-redux";
import { trackPromise } from "react-promise-tracker";
import { QueryIDsBusinessLeaderDashBoard } from "../../../../../../pages/AssetManagement/Overview/BusinessLeaderDashboard/logic/QueryIDsBusinessLeaderDashBoard";
import { buiDashboardEndPointLogic } from "../../../../../../logic/AssetManagement/overview/BusinessLeaderDashboard/buiDashboardEndPointLogic";
import DownloadIcon from "@mui/icons-material/Download";
import { saveAs } from "file-saver";
import { BLDTableSkeleton } from "../../helpers/BLDSkeletons";

export const TopKeywords = React.memo(() => {
  const [topKeyWordData, setTopKeyWordData] = useState([]) as any;
  const [isSectionLoading, setIsSectionLoading] = useState(true);
  const authToken = useSelector(selectUserToken)?.toString();

  const {
    selectedSharedUidBui,
    selectedDimensions,
    selectedType,
    selectedAll,
  } = useSelector((state: any) => state?.businessLeaderDashBoard);
  const tableHeadings = additionalQueriestableHeadings05;

  // for alert message
  const datesForFirstTimeLoading = getSelectedTimePeriod("MTD"); // for first time loading
  const [savedStartDate, setSavedStartDate] = useState(
    datesForFirstTimeLoading.startDate,
  ) as any;
  const [savedEndDate, setSavedEndDate] = useState(
    datesForFirstTimeLoading.endDate,
  ) as any;

  const [selectedTimeFrame, setSelectedTimeFrame] = useState("MTD") as any;
  const [selectedKeyWord, setSelectedKeyWord] = useState(null) as any;

  const headCells = Object.entries(tableHeadings);

  const [filteredData, setFilteredData] = useState([]);
  const [allKeyWordRows, setAllKeyWordRows] = useState([]);

  const [order, setOrder] = useState("desc") as any;
  const [orderBy, setOrderBy] = useState("cost") as any;

  const getTopKeyWordData = async (startDate?: string, endDate?: string) => {
    try {
      setIsSectionLoading(true);
      let currentStartDate = savedStartDate;
      let currentEndDate = savedEndDate;
      if (startDate && endDate) {
        currentStartDate = startDate;
        currentEndDate = endDate;
      }
      await buiDashboardEndPointLogic.getBuiDashboardData_topKeyWord(
        authToken as string,
        selectedSharedUidBui,
        QueryIDsBusinessLeaderDashBoard.topKeyWordId,
        trackPromise,
        setTopKeyWordData,
        selectedDimensions,
        selectedType,
        selectedAll,
        currentStartDate,
        currentEndDate,
      );
    } catch (error: any) {
      console.error("Error getting BUI dashboard data", error);
    } finally {
      setIsSectionLoading(false);
    }
  };

  useEffect(() => {
    getTopKeyWordData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (topKeyWordData?.query_output?.length > 0) {
      setFilteredData(topKeyWordData?.query_output);
      setAllKeyWordRows(
        topKeyWordData?.query_output?.map((obj: any) => obj?.kw_name),
      );
      handleRequestSort("cost", "asc"); // sending the opposite from desc so it will toggle it
    } else {
      setFilteredData([]);
      setAllKeyWordRows([]);
    }
    // eslint-disable-next-line
  }, [topKeyWordData]);

  const parseValue = (value: any) => {
    if (typeof value === "string") {
      if (value.includes("%")) {
        return parseFloat(value.replace("%", ""));
      }
      if (value.includes("$")) {
        return parseFloat(value.replace("$", ""));
      } else {
        return value.toLowerCase();
      }
    } else if (value === null || !value) {
      return "";
    }
    return value;
  };

  const handleRequestSort = (property: any, orderRender: any) => {
    const isAscending = orderBy === property && orderRender === "asc";
    const newOrder = isAscending ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);

    const sortedRows = [...topKeyWordData?.query_output].sort((a, b) => {
      const aValue = parseValue(a[orderBy]);
      const bValue = parseValue(b[orderBy]);

      if (aValue < bValue) {
        return newOrder === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return newOrder === "asc" ? 1 : -1;
      }
      return 0;
    }) as any;

    setFilteredData(sortedRows);
  };

  const timeFrame = [
    { label: "MTD" },
    { label: "Yesterday" },
    { label: "Last 7 Days" },
    { label: "Last Month" },
    { label: "YTD" },
  ];

  const handleTimeFrameChange = (_: unknown, value: any) => {
    setSelectedTimeFrame(value?.label);
    setSelectedKeyWord(null);
    const dates = getSelectedTimePeriod(value?.label);
    if (dates.endDate && dates.startDate) {
      getTopKeyWordData(dates.startDate, dates.endDate);
      setSavedStartDate(dates.startDate);
      setSavedEndDate(dates.endDate);
    } else {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "No Dates Choosen",
          type: "error",
        }),
      );
    }
  };

  // filter data by key word
  const handleKeyWordChange = (_: unknown, value: any) => {
    setSelectedKeyWord(value);
    if (value === null) {
      setFilteredData(topKeyWordData?.query_output);
      handleRequestSort("cost", "asc"); // sending the opposite from desc so it will toggle it
    } else {
      const filtered = filteredData?.filter(
        (row: any) => row?.kw_name === value,
      );
      setFilteredData(filtered);
    }
  };

  const handleExportCSV = () => {
    const csvRows = [];

    // Extract the table headers
    const headers = headCells.map((pair) => pair[0]) as any;
    csvRows.push(headers.join(","));

    // Extract the table data
    // eslint-disable-next-line
    filteredData.map((row: any) => {
      let rowData = [] as any;
      rowData.push(row?.kw_name);
      rowData.push(row?.matchtype);
      rowData.push(row?.adgroup_name);
      rowData.push(row?.campaign_name);
      rowData.push(row?.traffic_source);
      rowData.push(row?.tier);
      rowData.push(Math.round(row?.cost) + "$");
      rowData.push(Math.round(row?.imp_share * 100) + "%");
      rowData.push(Math.round(row?.FTD));
      rowData.push(Math.round(row?.CAC) + "$");
      rowData.push(Math.round(row?.CVR * 100) + "%");
      csvRows.push(rowData.join(","));
    });

    // Convert CSV rows to a single string
    const csvString = csvRows.join("\n");

    // Create a blob and download the file
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "TopKeywords.csv");
  };

  return (
    <>
      <Grid alignItems={"center"} container columnSpacing={2} mb={1}>
        <Grid item xs={12} mb={2}>
          <Stack flexDirection={"row"} alignItems="center" gap={2}>
            <Alert
              sx={{
                color: "#ED6C02",
                margin: "16px",
                border: "1px solid #ED6C0280",
                width: "100%",
              }}
              severity="warning"
              icon={<InfoOutlinedIcon fontSize="inherit" sx={{ mr: 1 }} />}
            >
              PLEASE NOTE: Data is only relevant for the period of{" "}
              {dayjs(savedStartDate).format("DD-MM-YYYY")} -{" "}
              {dayjs(savedEndDate).format("DD-MM-YYYY")}
            </Alert>
            <IconButton color="secondary" size="large" data-tracker="279">
              <DownloadIcon fontSize="medium" onClick={handleExportCSV} />
            </IconButton>
          </Stack>
        </Grid>
        <Grid item xs={8} px={2}></Grid>
        <Grid item xs={2}>
          <Autocomplete
            data-tracker="286"
            sx={{ width: "100%" }}
            onChange={handleKeyWordChange}
            options={allKeyWordRows}
            value={selectedKeyWord}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Key Word"
                label="Filter by Key Word"
                color="secondary"
              />
            )}
            size="small"
          />
        </Grid>
        <Grid item xs={2}>
          <Autocomplete
            data-tracker="89"
            sx={{ width: "100%" }}
            onChange={handleTimeFrameChange}
            options={timeFrame}
            value={selectedTimeFrame}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Time Frame"
                label="Time Frame"
                color="secondary"
              />
            )}
            size="small"
          />
        </Grid>
      </Grid>

      {isSectionLoading ? (
        <BLDTableSkeleton columns={headCells.length} showToolbar={false} />
      ) : (
      <TableContainer
        className="RunRateTableWrap"
        component={Paper}
        sx={{ boxShadow: "none", maxHeight: 400, overflowY: "auto" }}
      >
        {filteredData?.length > 0 ? (
          <>
            <Table aria-label="collapsible table" size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headCells.map(([key, value], index) => (
                    <TableCell
                      key={index + "headCellTop" + Math.random()}
                      align="left"
                    >
                      <TableSortLabel
                        active={orderBy === key}
                        direction={orderBy === key ? order : "asc"}
                        onClick={() => handleRequestSort(key, order)}
                      >
                        {value}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData?.map((row: any, rowIndex: any) => (
                  <React.Fragment
                    key={rowIndex + "filteredTop" + Math.random()}
                  >
                    <TableRow
                      sx={{
                        "&:not(:last-child) > *": {
                          borderBottom:
                            "1px solid rgba(224, 224, 224, 1) !important",
                        },
                      }}
                    >
                      <TableCell
                        key={1 + "tabelCellTop" + Math.random()}
                        align="left"
                      >
                        {row?.kw_name}
                      </TableCell>
                      <TableCell
                        key={2 + "tabelCellTop" + Math.random()}
                        align="left"
                      >
                        {row?.matchtype}
                      </TableCell>
                      <TableCell
                        key={3 + "tabelCellTop" + Math.random()}
                        align="left"
                      >
                        {row?.adgroup_name}
                      </TableCell>
                      <TableCell
                        key={4 + "tabelCellTop" + Math.random()}
                        align="left"
                      >
                        {row?.campaign_name}
                      </TableCell>
                      <TableCell
                        key={5 + "tabelCellTop" + Math.random()}
                        align="left"
                      >
                        {row?.traffic_source}
                      </TableCell>
                      <TableCell
                        key={6 + "tabelCellTop" + Math.random()}
                        align="left"
                      >
                        {row?.tier}
                      </TableCell>
                      <TableCell
                        key={7 + "tabelCellTop" + Math.random()}
                        align="left"
                      >
                        {roundStringNumberToInt(row?.cost) + "$"}
                      </TableCell>
                      <TableCell
                        key={8 + "tabelCellTop" + Math.random()}
                        align="left"
                      >
                        {roundStringNumberToInt(row?.imp_share * 100) + "%"}
                      </TableCell>
                      <TableCell
                        key={9 + "tabelCellTop" + Math.random()}
                        align="left"
                      >
                        {roundStringNumberToInt(row?.FTD)}
                      </TableCell>
                      <TableCell
                        key={10 + "tabelCellTop" + Math.random()}
                        align="left"
                      >
                        {roundStringNumberToInt(row?.CAC) + "$"}
                      </TableCell>
                      <TableCell
                        key={11 + "tabelCellTop" + Math.random()}
                        align="left"
                      >
                        {roundStringNumberToInt(row?.CVR * 100) + "%"}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </>
        ) : (
          <Alert
            sx={{
              color: "#ED6C02",
              margin: "16px",
              border: "1px solid #ED6C0280",
            }}
            severity="warning"
            icon={<InfoOutlinedIcon fontSize="inherit" sx={{ mr: 1 }} />}
          >
            NO DATA
          </Alert>
        )}
      </TableContainer>
      )}
    </>
  );
});
